import React, { useState, useEffect } from 'react';
import { statsApi, languagesApi } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Brain, AlertTriangle, Eye, MousePointerClick, Layers, Globe, TrendingDown, Shield } from 'lucide-react';

const STATUS_COLORS = {
  mastered: '#22c55e',
  known: '#60a5fa',
  familiar: '#f59e0b',
  learning: '#f97316',
  new: '#ef4444',
  ignored: '#6b7280',
};

export default function MemoryAwareness() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState('');

  useEffect(() => {
    languagesApi.getAll().then(r => setLanguages(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    statsApi.getMemoryAwareness(selectedLang || null)
      .then(r => setData(r.data))
      .catch(e => { console.error(e); toast.error('Failed to load memory data'); })
      .finally(() => setLoading(false));
  }, [selectedLang]);

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-32" /><Skeleton className="h-32" /><Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const dist = data.strength_distribution || {};
  const total = data.total_words || 1;
  const alerts = data.language_alerts || [];
  const decaying = data.decaying_soon || [];
  const agg = data.aggregates || {};
  const langHealth = data.language_health || {};

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="memory-awareness">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Brain className="h-6 w-6 text-purple-400" />
          <h2 className="text-xl font-bold">Memory Awareness</h2>
          <Badge variant="secondary" className="font-mono text-xs">{data.total_words} words</Badge>
        </div>
        <Select value={selectedLang || 'all'} onValueChange={(v) => setSelectedLang(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[180px]" data-testid="lang-filter">
            <SelectValue placeholder="All languages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All languages</SelectItem>
            {languages.filter(l => langHealth[l.code]).map(l => (
              <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2" data-testid="language-alerts">
          {alerts.map((alert, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
              <span className="text-sm">
                <span className="font-semibold text-amber-400 uppercase">{alert.language_code}</span>{' '}
                {alert.message}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Strength Distribution */}
      <Card data-testid="strength-distribution">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Shield className="h-4 w-4 text-blue-400" />
            Strength Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {['mastered', 'known', 'familiar', 'learning', 'new'].map(status => {
              const count = dist[status] || 0;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={status} className="flex items-center gap-3">
                  <span className="text-xs w-16 capitalize" style={{ color: STATUS_COLORS[status] }}>{status}</span>
                  <div className="flex-1 h-3 bg-[var(--iling-hover)] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${pct}%`, backgroundColor: STATUS_COLORS[status] }}
                    />
                  </div>
                  <span className="text-xs font-mono w-16 text-right text-[var(--iling-muted)]">
                    {count} ({pct}%)
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 text-center">
            <Eye className="h-5 w-5 mx-auto mb-1 text-blue-400" />
            <div className="text-2xl font-bold font-mono">{agg.total_exposures?.toLocaleString()}</div>
            <div className="text-[10px] text-[var(--iling-muted)]">Total Exposures</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <MousePointerClick className="h-5 w-5 mx-auto mb-1 text-amber-400" />
            <div className="text-2xl font-bold font-mono">{agg.total_clicks?.toLocaleString()}</div>
            <div className="text-[10px] text-[var(--iling-muted)]">Lookups (Weakness)</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <Layers className="h-5 w-5 mx-auto mb-1 text-cyan-400" />
            <div className="text-2xl font-bold font-mono">{agg.avg_context_diversity}</div>
            <div className="text-[10px] text-[var(--iling-muted)]">Avg Context Diversity</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <TrendingDown className="h-5 w-5 mx-auto mb-1 text-red-400" />
            <div className="text-2xl font-bold font-mono">{data.decaying_count}</div>
            <div className="text-[10px] text-[var(--iling-muted)]">Decaying This Week</div>
          </CardContent>
        </Card>
      </div>

      {/* Language Health */}
      {Object.keys(langHealth).length > 1 && !selectedLang && (
        <Card data-testid="language-health">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Globe className="h-4 w-4 text-emerald-400" />
              Language Balance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(langHealth)
                .sort((a, b) => b[1].total - a[1].total)
                .map(([code, health]) => (
                  <div key={code} className="flex items-center gap-3">
                    <span className="text-xs font-bold uppercase w-8">{code}</span>
                    <div className="flex-1">
                      <div className="flex justify-between text-[10px] text-[var(--iling-muted)] mb-0.5">
                        <span>{health.total} words</span>
                        <span>
                          {health.decaying > 0 && (
                            <span className="text-amber-400 mr-2">{health.decaying} decaying</span>
                          )}
                          {health.days_since_activity != null && (
                            <span>{health.days_since_activity < 1 ? 'Active today' : `${Math.round(health.days_since_activity)}d ago`}</span>
                          )}
                        </span>
                      </div>
                      <div className="h-2 bg-[var(--iling-hover)] rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.min(100, health.avg_strength * 100)}%`,
                            backgroundColor: health.avg_strength > 0.65 ? '#22c55e' : health.avg_strength > 0.35 ? '#f59e0b' : '#ef4444'
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-xs font-mono w-12 text-right">{Math.round(health.avg_strength * 100)}%</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Decaying Words */}
      {decaying.length > 0 && (
        <Card data-testid="decaying-words">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-red-400" />
              Words Decaying Soon
              <Badge variant="destructive" className="text-[10px]">{data.decaying_count}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {decaying.slice(0, 40).map((w, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border transition-colors hover:bg-[var(--iling-hover)]"
                  style={{ borderColor: STATUS_COLORS[w.current_status] + '50', color: STATUS_COLORS[w.current_status] }}
                  title={`${w.current_status} → ${w.predicted_status} | strength: ${Math.round(w.effective_strength * 100)}%`}
                >
                  {w.language_code !== selectedLang && <span className="text-[9px] opacity-50 uppercase">{w.language_code}</span>}
                  {w.word}
                </span>
              ))}
              {data.decaying_count > 40 && (
                <span className="text-xs text-[var(--iling-muted)] self-center">+{data.decaying_count - 40} more</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vocabulary Heatmap */}
      {data.heatmap?.length > 0 && (
        <Card data-testid="vocabulary-heatmap">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Vocabulary Heatmap</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-0.5">
              {data.heatmap.slice(0, 300).map((w, i) => (
                <div
                  key={i}
                  className="w-3 h-3 rounded-sm cursor-default transition-transform hover:scale-150"
                  style={{ backgroundColor: STATUS_COLORS[w.status] || '#333' }}
                  title={`${w.word} (${w.language_code}) — ${w.status} ${Math.round(w.strength * 100)}%`}
                />
              ))}
            </div>
            <div className="flex gap-4 mt-3 text-[10px] text-[var(--iling-muted)]">
              {['mastered', 'known', 'familiar', 'learning', 'new'].map(s => (
                <span key={s} className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: STATUS_COLORS[s] }} />
                  {s}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
